import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ChevronDown, ChevronUp, Lightbulb, ThumbsDown, Wrench } from 'lucide-react';
import { Button } from '@/components/Button';
import { Skeleton } from '@/components/Skeleton';
import { cn } from '@/lib/cn';
import { chaves } from '@/features/tickets/tickets.api';
import { AvisoDeFalha } from '@/features/workflow/componentes';
import { classificarFalha, type Falha } from '@/features/workflow/recusa';
import { MotivosDaSugestao, Recorrencia } from '@/features/kedb/componentes';
import { aplicarContorno, chavesDeKedb, obterSugestoes } from '@/features/kedb/kedb.api';
import { LinkDoErroConhecido } from '@/features/kedb/navegacao';
import type { SugestaoDeErro } from '@/features/kedb/tipos';

/**
 * ★ "Já sabemos disto?" — a pergunta que a Central responde sozinha.
 *
 * Este é o critério 4.15, e é onde a Base de Erros Conhecidos deixa de ser um
 * arquivo morto. O operador abre um incidente parecido com algo que já se viu
 * quatro vezes, e a Central diz **antes do diagnóstico**: já sabemos disto, e o
 * contorno é este.
 *
 * ## As três decisões de interface que fazem isso funcionar
 *
 *   1. **aparece sozinha, no alto do chamado.** Não é uma aba, não é um botão
 *      "procurar na base": um erro conhecido que depende de alguém lembrar de
 *      procurá-lo é um campo de texto caro;
 *   2. **some quando não há nada.** A faixa que aparece sempre, às vezes vazia,
 *      é a faixa que se aprende a ignorar — e aí ela também não é vista no dia
 *      em que traz a resposta;
 *   3. **a recusa é registrada.** "Não serviu" grava `applied: false`, que não
 *      conta para o contador e não cria o vínculo com o problema. Sugestão
 *      recusada é o sinal de que a base está devolvendo ruído, e sem registro
 *      ninguém descobre.
 *
 * A pontuação vem do servidor e não é reinventada aqui: texto, mesmo item de
 * configuração, mesmo serviço e recorrência, com pesos calibrados em
 * `kedb/domain/similaridade.ts`. A tela mostra **por que** cada sugestão veio,
 * porque "porque o relato se parece" e "porque é o mesmo switch" pedem
 * confianças diferentes de quem lê.
 */
export function SugestoesDeErroConhecido({ ticketId }: { ticketId: string }) {
  const consulta = useQuery({
    queryKey: chavesDeKedb.sugestoes(ticketId),
    queryFn: () => obterSugestoes(ticketId),
    // Sem retentativa: 403 aqui é resposta definitiva, e insistir só atrasaria
    // a abertura do chamado para quem não pode ler a base.
    retry: false,
    staleTime: 60_000,
  });

  if (consulta.isPending) return <Skeleton className="h-16 w-full" />;

  // Falha na sugestão não vira alarme no topo do chamado: ela é um apoio, e um
  // apoio que falha não pode roubar a atenção de quem está atendendo.
  if (consulta.isError || consulta.data.length === 0) return null;

  return (
    <section
      aria-labelledby="titulo-sugestoes"
      className="space-y-2 rounded-lg border border-primary/25 bg-primary/5 px-4 py-3"
    >
      <h2 id="titulo-sugestoes" className="flex items-center gap-2 text-sm font-semibold">
        <Lightbulb className="h-4 w-4 text-primary" aria-hidden />
        Já sabemos disto?
        <span className="font-normal text-muted-foreground">
          {consulta.data.length} erro(s) conhecido(s) parecido(s) com este chamado
        </span>
      </h2>

      <ul className="space-y-2">
        {consulta.data.map((sugestao) => (
          <Sugestao key={sugestao.id} sugestao={sugestao} ticketId={ticketId} />
        ))}
      </ul>
    </section>
  );
}

function Sugestao({ sugestao, ticketId }: { sugestao: SugestaoDeErro; ticketId: string }) {
  const cliente = useQueryClient();
  const [aberta, setAberta] = useState(false);
  const [falha, setFalha] = useState<Falha | null>(null);
  const [feito, setFeito] = useState<'aplicado' | 'recusado' | null>(null);

  const registrar = useMutation({
    mutationFn: (applied: boolean) => aplicarContorno(sugestao.id, { ticketId, applied }),
    onMutate: () => setFalha(null),
    onSuccess: (_resposta, applied) => {
      setFeito(applied ? 'aplicado' : 'recusado');
      // Aplicar o contorno cria o vínculo tipado com o problema e mexe no
      // contador do erro conhecido: as duas raízes recarregam.
      void cliente.invalidateQueries({ queryKey: chaves.raiz });
      void cliente.invalidateQueries({ queryKey: chavesDeKedb.raiz });
    },
    onError: (erro) => setFalha(classificarFalha(erro)),
  });

  return (
    <li className="space-y-2 rounded-md border border-border bg-card px-3 py-2">
      <div className="flex flex-wrap items-center gap-2 text-sm">
        <LinkDoErroConhecido
          erroId={sugestao.id}
          className="font-mono text-xs font-semibold text-primary hover:underline"
        >
          {sugestao.code}
        </LinkDoErroConhecido>
        <span className="min-w-0 flex-1 truncate font-medium">{sugestao.title}</span>
        <Recorrencia contagem={sugestao.occurrenceCount} />
        <Confianca score={sugestao.score} />
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <MotivosDaSugestao motivos={sugestao.reasons} />
        <button
          type="button"
          onClick={() => setAberta((atual) => !atual)}
          aria-expanded={aberta}
          className="ml-auto inline-flex items-center gap-1 text-xs font-medium text-primary hover:underline"
        >
          {aberta ? 'Ocultar contorno' : 'Ver contorno'}
          {aberta ? (
            <ChevronUp className="h-3 w-3" aria-hidden />
          ) : (
            <ChevronDown className="h-3 w-3" aria-hidden />
          )}
        </button>
      </div>

      {aberta ? (
        <div className="space-y-2 border-t border-border pt-2">
          <div>
            <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              Sintomas
            </p>
            <p className="whitespace-pre-wrap text-sm text-muted-foreground">{sugestao.symptoms}</p>
          </div>
          <div className="rounded-md border border-success/25 bg-success/5 px-3 py-2">
            <p className="text-xs font-semibold uppercase tracking-wider text-success">
              Solução de contorno
            </p>
            <p className="mt-1 whitespace-pre-wrap text-sm leading-relaxed">{sugestao.workaround}</p>
          </div>
        </div>
      ) : null}

      {feito === null ? (
        <div className="flex flex-wrap items-center gap-2">
          <Button
            size="sm"
            loading={registrar.isPending}
            onClick={() => registrar.mutate(true)}
            icon={<Wrench className="h-4 w-4" aria-hidden />}
          >
            Apliquei este contorno
          </Button>
          <Button
            size="sm"
            variant="ghost"
            loading={registrar.isPending}
            onClick={() => registrar.mutate(false)}
            icon={<ThumbsDown className="h-4 w-4" aria-hidden />}
          >
            Não serviu
          </Button>
          {sugestao.alreadyLinked ? (
            <span className="text-xs text-muted-foreground">
              já registrado neste chamado — registrar de novo sobrescreve a decisão anterior
            </span>
          ) : null}
        </div>
      ) : (
        <p
          className={cn(
            'text-xs font-semibold',
            feito === 'aplicado' ? 'text-success' : 'text-muted-foreground',
          )}
        >
          {feito === 'aplicado'
            ? 'Registrado. O chamado foi ligado ao problema de origem, e o contador do erro conhecido subiu.'
            : 'Registrado como não aplicável. O vínculo com o problema não foi criado, e o contador não mudou.'}
        </p>
      )}

      {falha ? <AvisoDeFalha falha={falha} /> : null}
    </li>
  );
}

/**
 * A confiança em palavra, e não só em número.
 *
 * A escala é a do servidor (0 a 100). O rótulo existe porque "72" sozinho não
 * diz a quem lê se vale abrir — e a decisão de abrir é a única que a pontuação
 * precisa apoiar.
 */
function Confianca({ score }: { score: number }) {
  const alta = score >= 60;
  const media = score >= 35;
  return (
    <span
      className={cn(
        'whitespace-nowrap rounded-full px-2 py-0.5 text-xs font-semibold ring-1 ring-inset',
        alta && 'bg-success/12 text-success ring-success/25',
        !alta && media && 'bg-warning/15 text-warning ring-warning/30',
        !media && 'bg-muted text-muted-foreground ring-border',
      )}
      title={`Pontuação ${score} de 100, calculada pelo servidor a partir do texto, do item de configuração, do serviço e da recorrência.`}
    >
      {alta ? 'muito parecido' : media ? 'parecido' : 'pode ser'} · {score}
    </span>
  );
}
